import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { formatAccountingAmount, downloadCsv } from '../../../utils/formatters';
import { EmptyRecordsState } from '../../common/ResponsiveRecordList';

const varianceColor = (v) => {
  if (v === undefined || v === null || v === 0) return 'text.primary';
  return v > 0 ? 'success.main' : 'error.main';
};

const SummaryTile = ({ label, value, color }) => (
  <Card variant="outlined" sx={{ flex: '1 1 200px' }}>
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums', color }}>
        {formatAccountingAmount(value)}
      </Typography>
    </CardContent>
  </Card>
);

/**
 * Renders IncomeStatementService's response: a single pre-ordered `lines`
 * array (class -> type subtotal rows interleaved with detail rows, already
 * indented/flagged by the API via `indent`/`is_total`) plus top-level
 * income/expense/profit totals.
 */
const IncomeStatementReport = ({ data, loading }) => {
  if (loading) {
    return <Typography color="text.secondary">Generating income statement...</Typography>;
  }
  if (!data) {
    return <EmptyRecordsState message="Choose a period and generate the report." />;
  }

  const lines = Array.isArray(data.lines) ? data.lines : [];
  const hasCompare = Boolean(data.compare_name);
  const profitColor = (data.net_profit || 0) >= 0 ? 'success.main' : 'error.main';

  const handleExport = () => {
    const headers = ['Account', 'Amount'];
    const rows = lines.map((l) => [l.label, l.amount]);
    rows.push(['Total Income', data.total_income]);
    rows.push(['Total Expenses', data.total_expenses]);
    rows.push(['Net Profit', data.net_profit]);
    downloadCsv(`income-statement-${data.period_name || 'report'}.csv`, headers, rows);
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">Income Statement</Typography>
          <Typography variant="body2" color="text.secondary">
            {data.period_name}{hasCompare ? ` (vs ${data.compare_name})` : ''}
          </Typography>
        </Box>
        <Button size="small" startIcon={<DownloadIcon />} onClick={handleExport} disabled={!lines.length}>
          Export CSV
        </Button>
      </Stack>

      <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
        <SummaryTile label="Total Income" value={data.total_income} color="success.main" />
        <SummaryTile label="Total Expenses" value={data.total_expenses} color="error.main" />
        <SummaryTile label="Net Profit" value={data.net_profit} color={profitColor} />
      </Stack>

      {lines.length === 0 ? (
        <EmptyRecordsState message="No income or expense activity for this period." />
      ) : (
        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>Account</TableCell>
                {hasCompare && <TableCell sx={{ fontWeight: 700 }} align="right">{data.compare_name}</TableCell>}
                {hasCompare && <TableCell sx={{ fontWeight: 700 }} align="right">Variance</TableCell>}
                {hasCompare && <TableCell sx={{ fontWeight: 700 }} align="right">Var %</TableCell>}
                <TableCell sx={{ fontWeight: 700 }} align="right">{data.period_name}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line, i) => (
                <TableRow key={i} hover={!line.is_total} sx={line.is_total ? { bgcolor: 'action.selected' } : undefined}>
                  <TableCell sx={{ pl: 2 + (line.indent || 0) * 2, fontWeight: line.is_total ? 700 : 400 }}>
                    {line.label}
                  </TableCell>
                  {hasCompare && (
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                      {line.compare_amount !== undefined ? formatAccountingAmount(line.compare_amount) : ''}
                    </TableCell>
                  )}
                  {hasCompare && (
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: varianceColor(line.variance) }}>
                      {line.variance !== undefined ? formatAccountingAmount(line.variance) : ''}
                    </TableCell>
                  )}
                  {hasCompare && (
                    <TableCell align="right" sx={{ color: varianceColor(line.variance) }}>
                      {line.variance_pct !== undefined && line.variance_pct !== null ? `${line.variance_pct.toFixed(1)}%` : ''}
                    </TableCell>
                  )}
                  <TableCell align="right" sx={{ fontWeight: line.is_total ? 700 : 400, fontVariantNumeric: 'tabular-nums' }}>
                    {formatAccountingAmount(line.amount, { blankZero: true })}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'primary.contrastText', fontWeight: 700 }}>NET PROFIT</TableCell>
                {hasCompare && <TableCell sx={{ color: 'primary.contrastText' }} />}
                {hasCompare && <TableCell sx={{ color: 'primary.contrastText' }} />}
                {hasCompare && <TableCell sx={{ color: 'primary.contrastText' }} />}
                <TableCell align="right" sx={{ color: 'primary.contrastText', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {formatAccountingAmount(data.net_profit)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default IncomeStatementReport;
